import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output, OnInit, forwardRef, ChangeDetectorRef } from "@angular/core";
import { CommonModule } from "@angular/common";
import { ControlValueAccessor, FormControl, FormGroup, NG_VALUE_ACCESSOR, ReactiveFormsModule } from "@angular/forms";
import { QuillModule } from 'ngx-quill';
import { Subject, takeUntil } from "rxjs";
import { UtilCommon } from "../../common/utils/UtilCommon";

@Component({
    selector: "app-quill-editor",
    standalone: true,
    imports: [
        CommonModule,
        QuillModule,
        ReactiveFormsModule,
    ],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => QuillEditorComponent),
            multi: true
        }
    ],
    templateUrl: "./quill-editor.component.html",
    styleUrl: "./quill-editor.component.scss",
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class QuillEditorComponent implements OnInit, ControlValueAccessor {

    reactiveForm = new FormGroup({
        editorControl: new FormControl<string>(''),
    });

    @Input() set data(value: string) {
        this.writeValue(value);
    }

    @Input() placeholder = '';
    @Input() readOnly = false;
    @Input() set height(value: string) {
        this._height = value;
        this.updateHeightVariable();
    }
    get height(): string {
        return this._height;
    }
    private _height = '300px';
    @Input() theme: 'snow' | 'bubble' = 'snow';

    @Input() hasError = false;

    @Output() editorChange = new EventEmitter<string>();
    @Output() editorCreated = new EventEmitter<any>();

    quillEditor?: any;
    private destroy$ = new Subject<void>();

    // ControlValueAccessor implementation
    private onChange = (value: string) => { };
    private onTouched = () => { };

    constructor(private cdr: ChangeDetectorRef) { }

    ngOnInit(): void {
        this.reactiveForm.get('editorControl')?.valueChanges
            .pipe(takeUntil(this.destroy$))
            .subscribe(value => {
                if (value) {
                    value = UtilCommon.sanitizeHtml(value);
                }
                this.editorChange.emit(value);
                this.onChange(value);
            });

        // Set initial height
        this.updateHeightVariable();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    // Custom function to convert headings and remove styling
    private toPlainText(): void {
        if (this.readOnly) return;

        const currentContent = this.getHTML();
        const plainContent = this.convertHeadingsToDivsAndRemoveStyling(UtilCommon.sanitizeHtml(currentContent));

        // Update the editor content directly
        if (this.quillEditor) {
            this.quillEditor.clipboard.dangerouslyPasteHTML(0, plainContent);

            // Trigger change event
            this.reactiveForm.get('editorControl')?.setValue(plainContent, { emitEvent: true });
        }
    }

    private convertHeadingsToDivsAndRemoveStyling(htmlString: string): string {
        if (!htmlString) return '';

        const parser = new DOMParser();
        const doc = parser.parseFromString(htmlString, 'text/html');

        // Convert headings to paragraphs (better for Quill)
        const headingElements = doc.querySelectorAll('h1, h2, h3, h4, h5, h6');
        headingElements.forEach(heading => {
            const p = document.createElement('p');
            p.textContent = heading.textContent || '';
            // Don't preserve class/id for plain text conversion
            heading.parentNode?.replaceChild(p, heading);
        });

        // Remove all inline styles and unwanted attributes
        doc.querySelectorAll('*').forEach(element => {
            element.removeAttribute('style');
            element.removeAttribute('class');
            element.removeAttribute('color');
            element.removeAttribute('background-color');
            element.removeAttribute('font-size');
            element.removeAttribute('font-family');

            // Remove data attributes that might contain formatting
            Array.from(element.attributes).forEach(attr => {
                if (attr.name.startsWith('data-')) {
                    element.removeAttribute(attr.name);
                }
            });
        });

        // Clean up empty elements
        doc.querySelectorAll('span, div').forEach(element => {
            if (!element.textContent?.trim()) {
                element.remove();
            } else if (element.tagName === 'SPAN' || element.tagName === 'DIV') {
                // Convert spans and divs to paragraphs
                const p = document.createElement('p');
                p.textContent = element.textContent;
                element.parentNode?.replaceChild(p, element);
            }
        });

        return doc.body.innerHTML || htmlString;
    }

    private updateHeightVariable(): void {
        if (typeof document !== 'undefined') {
            document.documentElement.style.setProperty('--editor-height', this.height);
        }
    }

    // Color presets (from your xhunt-editor constant)
    private colorPresets = [
        '#b60205', '#d93f0b', '#fbca04', '#0e8a16', '#006b75', '#1d76db', '#0052cc', '#5319e7',
        '#e99695', '#f9d0c4', '#fef2c0', '#c2e0c6', '#bfdadc', '#c5def5', '#bfd4f2', '#d4c5f9',
        '#ff5733', '#33ff57', '#ff69b4', '#242424', '#ffffff', '#33a1ff', '#ff8c00', '#000000',
        // Additional common colors
        '#ff0000', '#00ff00', '#0000ff', '#ffff00', '#ff00ff', '#00ffff', '#800000', '#008000',
        '#000080', '#808000', '#800080', '#008080', '#c0c0c0', '#808080', '#9999ff', '#993366',
        '#ffffcc', '#ccffff', '#660066', '#ff8080', '#0066cc', '#ccccff', '#000080', '#ff00ff',
        '#ffff00', '#00ffff', '#800080', '#800000', '#008080', '#0000ff', '#00ccff', '#ccffcc'
    ];

    // Dynamic modules based on readOnly state
    get quillModules() {
        return this.readOnly ? { toolbar: false } : {
            toolbar: [
                ['bold', 'italic', 'underline', 'strike'],
                ['blockquote', 'code-block'],
                [{ 'color': this.colorPresets }, { 'background': this.colorPresets }],
                // [{ 'header': 1 }, { 'header': 2 }],
                // Header dropdown with all levels (h1-h6)
                [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
                [{ 'list': 'ordered' }, { 'list': 'bullet' }],
                [{ 'script': 'sub' }, { 'script': 'super' }],
                [{ 'indent': '-1' }, { 'indent': '+1' }],
                [{ 'size': ['small', false, 'large', 'huge'] }],
                // Color and background with presets
                [{ 'align': [] }],
                ['clean'],
                ['link', 'image'],
                ['plainText'],
            ]
        };
    }

    // ControlValueAccessor methods
    writeValue(value: string): void {
        if (value) {
            value = UtilCommon.sanitizeHtml(value);
            this.reactiveForm.get('editorControl')?.setValue(value || '', { emitEvent: false });
        } else {
            this.reactiveForm.get('editorControl')?.setValue('', { emitEvent: false });
        }
    }

    registerOnChange(fn: (value: string) => void): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    setDisabledState(isDisabled: boolean): void {
        this.readOnly = isDisabled;
        if (isDisabled) {
            this.reactiveForm.get('editorControl')?.disable({ emitEvent: false });
        } else {
            this.reactiveForm.get('editorControl')?.enable({ emitEvent: false });
        }
    }

    // Event handlers
    onEditorCreated(editor: any): void {
        this.quillEditor = editor;
        this.editorCreated.emit(editor);

        // Set up custom button after editor is created
        this.setupCustomButton();
    }

    private setupCustomButton(): void {
        if (!this.quillEditor) return;

        const toolbar = this.quillEditor.getModule('toolbar');
        if (!toolbar) return;

        // Find the plainText button and add click handler
        const plainTextButton = toolbar.container.querySelector('.ql-plainText') as HTMLElement;
        if (plainTextButton) {
            // Use broom icon instead of TXT text
            plainTextButton.innerHTML = `
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path d="M19.36,2.72L20.78,4.14L15.06,9.85C16.13,11.39 16.28,13.24 15.38,14.44L9.06,8.12C10.26,7.22 12.11,7.37 13.65,8.44L19.36,2.72M5.93,17.57C3.92,15.56 2.69,13.16 2.35,10.92L7.23,8.83L14.67,16.27L12.58,21.15C10.34,20.81 7.94,19.58 5.93,17.57Z" />
            </svg>
            `;
            plainTextButton.setAttribute('title', 'Clean Formatting (Remove Headings & Styling)');
            plainTextButton.style.width = '28px';
            plainTextButton.style.height = '28px';
            plainTextButton.style.display = 'inline-flex';
            plainTextButton.style.alignItems = 'center';
            plainTextButton.style.justifyContent = 'center';
            plainTextButton.style.border = 'none';
            plainTextButton.style.background = 'transparent';
            plainTextButton.style.cursor = 'pointer';
            plainTextButton.style.borderRadius = '3px';
            // plainTextButton.innerHTML = '<span style="font-size: 10px; font-weight: bold; padding: 2px;">TXT</span>';
            // plainTextButton.setAttribute('title', 'Convert to Plain Text (Remove Headings & Styling)');
            // plainTextButton.style.width = '28px';
            // plainTextButton.style.height = '28px';
            // plainTextButton.style.display = 'inline-flex';
            // plainTextButton.style.alignItems = 'center';
            // plainTextButton.style.justifyContent = 'center';

            // Remove any existing click handlers and add our custom one
            plainTextButton.onclick = (e) => {
                e.preventDefault();
                e.stopPropagation();
                this.toPlainText();
            };
        }
    }


    onSelectionChanged(event: any): void {
        this.onTouched();
    }

    // Utility methods
    focus(): void {
        this.quillEditor?.focus();
    }

    blur(): void {
        this.quillEditor?.blur();
    }

    getHTML(): string {
        return this.quillEditor?.root.innerHTML || '';
    }

    getText(): string {
        return this.quillEditor?.getText() || '';
    }

    getLength(): number {
        return this.quillEditor?.getLength() || 0;
    }
}